import type { DocumentData } from 'firebase/firestore'
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import { defineStore } from 'pinia'
import type { LiveSpec } from '~/utils/liveData'
import { acquireLive, liveDocListener } from '~/utils/liveData'

/**
 * System configuration stored in Firestore `system/config`
 */
export interface SystemConfig {
  nextParadeYear: number
  /** ISO date string (YYYY-MM-DD) for parade day */
  nextParadeDate?: string
  maintenanceMode?: boolean
  updatedAt?: Date
  updatedBy?: string
}

/**
 * Admin task trigger status
 */
export interface TaskTriggerStatus {
  running: boolean
  lastRefresh?: Date
  error?: string
  result?: unknown
  message?: string
  // Derivation-specific fields
  processingAt?: Date
  finishedAt?: Date
  run?: boolean
  done?: boolean
  processing?: boolean
  leaseHolder?: string
  batchNumber?: number
  totalProcessed?: number
  totalUpdated?: number
  resumeAfter?: string
  processed?: number
  changed?: number
  durationMs?: number
  // Discourse sync specific fields
  synced?: number
  skipped?: number
  errors?: number
}

function toConfig(data: DocumentData): SystemConfig {
  return {
    nextParadeYear: data.nextParadeYear,
    nextParadeDate: data.nextParadeDate ?? undefined,
    maintenanceMode: data.maintenanceMode ?? false,
    updatedAt: data.updatedAt?.toDate?.() ?? undefined,
    updatedBy: data.updatedBy ?? undefined,
  }
}

// The admin task trigger documents, and the state each one fills
const TASK_STATUS_DOCS = {
  stats_trigger: 'statsStatus',
  derive_trigger: 'derivationStatus',
  discourse_sync_trigger: 'discourseSyncStatus',
} as const

function toTaskStatus(id: keyof typeof TASK_STATUS_DOCS, data: DocumentData): TaskTriggerStatus {
  if (id === 'stats_trigger') {
    return {
      running: data.refreshRequested === true,
      lastRefresh: data.lastRefresh?.toDate?.() ?? undefined,
      error: data.error ?? undefined,
      result: data.result ?? undefined,
      message: data.message ?? undefined,
    }
  }
  const common: TaskTriggerStatus = {
    running: data.run === true,
    run: data.run,
    done: data.done ?? false,
    processing: data.processing ?? false,
    lastRefresh: data.finishedAt?.toDate?.() ?? undefined,
    finishedAt: data.finishedAt?.toDate?.() ?? undefined,
    processingAt: data.processingAt?.toDate?.() ?? undefined,
    error: data.error ?? undefined,
    processed: data.processed ?? undefined,
    durationMs: data.durationMs ?? undefined,
  }
  if (id === 'derive_trigger') {
    return {
      ...common,
      leaseHolder: data.leaseHolder ?? undefined,
      batchNumber: data.batchNumber ?? undefined,
      totalProcessed: data.totalProcessed ?? undefined,
      totalUpdated: data.totalUpdated ?? undefined,
      resumeAfter: data.resumeAfter ?? undefined,
      changed: data.changed ?? undefined,
    }
  }
  return {
    ...common,
    synced: data.synced ?? undefined,
    skipped: data.skipped ?? undefined,
    errors: data.errors ?? undefined,
  }
}

export const useSystemConfigStore = defineStore('systemConfig', {
  state: () => ({
    config: null as SystemConfig | null,
    loading: false,
    loaded: false,

    // Admin task statuses
    statsStatus: null as TaskTriggerStatus | null,
    derivationStatus: null as TaskTriggerStatus | null,
    discourseSyncStatus: null as TaskTriggerStatus | null,
  }),

  getters: {
    /**
     * Get parade year. `nextParadeDate` is the source of truth when present
     * (so updating just the date keeps the year consistent); falls back to
     * the explicit `nextParadeYear` field, then to the current calendar year.
     */
    paradeYear(): number {
      const dateStr = this.config?.nextParadeDate
      if (dateStr) {
        const y = Number.parseInt(dateStr.split('-')[0] ?? '', 10)
        if (Number.isFinite(y) && y > 0) return y
      }
      if (this.config?.nextParadeYear) {
        return this.config.nextParadeYear
      }
      // Fallback to current year
      return new Date().getFullYear()
    },

    /**
     * Get parade date as Date object (or null if not set)
     */
    paradeDate(): Date | null {
      if (this.config?.nextParadeDate) {
        // Parse YYYY-MM-DD as local date (not UTC)
        const parts = this.config.nextParadeDate.split('-').map(Number)
        const year = parts[0]
        const month = parts[1]
        const day = parts[2]
        if (year && month && day) {
          return new Date(year, month - 1, day)
        }
      }
      return null
    },

    isStatsRunning(): boolean {
      return this.statsStatus?.running ?? false
    },

    isDerivationRunning(): boolean {
      // Check processing flag, not processingAt (which persists after completion)
      return this.derivationStatus?.processing === true || (this.derivationStatus?.run === true && !this.derivationStatus?.done)
    },

    isDiscourseSyncRunning(): boolean {
      return this.discourseSyncStatus?.processing === true || (this.discourseSyncStatus?.run === true && !this.discourseSyncStatus?.done)
    },
  },

  actions: {
    /**
     * `system/config`, live: held for as long as the app runs (the rules let
     * anyone read it, signed in or not). The first call waits for it; later
     * ones answer at once.
     */
    async loadConfig(): Promise<SystemConfig | null> {
      this.loading = !this.loaded
      try {
        await acquireLive(this.configSpec(), { session: 'app' })
      }
      catch (error) {
        // The parade year falls back to the calendar year (paradeYear)
        console.error('[SystemConfig] Error loading config:', error)
      }
      finally {
        this.loaded = true
        this.loading = false
      }
      return this.config
    },

    configSpec(): LiveSpec<DocumentData> {
      const path = 'system/config'
      return {
        key: path,
        listen: (emit, fail) => onSnapshot(doc(useFirestore(), 'system', 'config'), ...liveDocListener(path, emit, fail)),
        apply: (changes) => {
          const data = changes[0]?.data
          if (data) this.config = toConfig(data)
        },
        evict: () => {
          this.config = null
        },
      }
    },

    /**
     * Update parade year (admin only)
     */
    async updateParadeYear(year: number, updatedBy: string): Promise<boolean> {
      try {
        const db = useFirestore()
        const configRef = doc(db, 'system', 'config')

        await setDoc(configRef, {
          nextParadeYear: year,
          updatedAt: serverTimestamp(),
          updatedBy,
        }, { merge: true })

        this.config = {
          ...this.config,
          nextParadeYear: year,
          updatedAt: new Date(),
          updatedBy,
        }

        return true
      }
      catch (error) {
        console.error('[SystemConfig] Error updating parade year:', error)
        return false
      }
    },

    /**
     * Update parade date (admin only)
     * Also updates nextParadeYear derived from the date
     * @param dateString - ISO date string YYYY-MM-DD
     */
    async updateParadeDate(dateString: string, updatedBy: string): Promise<boolean> {
      try {
        const db = useFirestore()
        const configRef = doc(db, 'system', 'config')

        // Derive year from date
        const year = Number.parseInt(dateString.split('-')[0] ?? '2026', 10)

        await setDoc(configRef, {
          nextParadeDate: dateString,
          nextParadeYear: year,
          updatedAt: serverTimestamp(),
          updatedBy,
        }, { merge: true })

        this.config = {
          ...this.config,
          nextParadeDate: dateString,
          nextParadeYear: year,
          updatedAt: new Date(),
          updatedBy,
        }

        return true
      }
      catch (error) {
        console.error('[SystemConfig] Error updating parade date:', error)
        return false
      }
    },

    /**
     * Update maintenance mode (admin only)
     */
    async updateMaintenanceMode(enabled: boolean, updatedBy: string): Promise<boolean> {
      try {
        const db = useFirestore()
        const configRef = doc(db, 'system', 'config')

        await setDoc(configRef, {
          maintenanceMode: enabled,
          updatedAt: serverTimestamp(),
          updatedBy,
        }, { merge: true })

        this.$patch((state) => {
          if (state.config) {
            state.config.maintenanceMode = enabled
            state.config.updatedBy = updatedBy
          }
        })

        return true
      }
      catch (error) {
        console.error('[SystemConfig] Error updating maintenance mode:', error)
        return false
      }
    },

    /**
     * The admin task statuses (stats refresh, derivation, Discourse sync),
     * live while the caller holds them: they update as the functions work,
     * where the settings page used to poll every 5 seconds.
     */
    async loadTaskStatuses(): Promise<void> {
      const ids = Object.keys(TASK_STATUS_DOCS) as (keyof typeof TASK_STATUS_DOCS)[]
      try {
        await Promise.all(ids.map(id => acquireLive(this.taskStatusSpec(id))))
      }
      catch (error) {
        console.error('[SystemConfig] Error loading task statuses:', error)
      }
    },

    taskStatusSpec(id: keyof typeof TASK_STATUS_DOCS): LiveSpec<DocumentData> {
      const path = `system/${id}`
      const field = TASK_STATUS_DOCS[id]
      return {
        key: path,
        listen: (emit, fail) => onSnapshot(doc(useFirestore(), 'system', id), ...liveDocListener(path, emit, fail)),
        apply: (changes) => {
          const data = changes[0]?.data
          if (data) this[field] = toTaskStatus(id, data)
        },
        evict: () => {
          this[field] = null
        },
      }
    },

    /**
     * Trigger stats refresh
     */
    async triggerStatsRefresh(requestedBy: string): Promise<boolean> {
      try {
        const db = useFirestore()
        const triggerRef = doc(db, 'system', 'stats_trigger')

        await setDoc(triggerRef, {
          refreshRequested: true,
          requestedAt: serverTimestamp(),
          requestedBy,
        }, { merge: true })

        // Update local status immediately
        this.statsStatus = {
          ...this.statsStatus,
          running: true,
        }

        return true
      }
      catch (error) {
        console.error('[SystemConfig] Error triggering stats refresh:', error)
        return false
      }
    },

    /**
     * Trigger mailchimp derivation refresh
     */
    async triggerDerivationRefresh(requestedBy: string): Promise<boolean> {
      try {
        const db = useFirestore()
        const triggerRef = doc(db, 'system', 'derive_trigger')

        await setDoc(triggerRef, {
          run: true,
          requestedAt: serverTimestamp(),
          requestedBy,
          // Reset progress fields
          batchNumber: 0,
          totalProcessed: 0,
          totalUpdated: 0,
          resumeAfter: null,
          error: null,
        }, { merge: true })

        // Update local status immediately
        this.derivationStatus = {
          ...this.derivationStatus,
          running: true,
          run: true,
        }

        return true
      }
      catch (error) {
        console.error('[SystemConfig] Error triggering derivation refresh:', error)
        return false
      }
    },

    /**
     * Trigger Discourse sync for all members
     */
    async triggerDiscourseSync(requestedBy: string, force = false): Promise<boolean> {
      try {
        const db = useFirestore()
        const triggerRef = doc(db, 'system', 'discourse_sync_trigger')

        await setDoc(triggerRef, {
          run: true,
          requestedAt: serverTimestamp(),
          requestedBy,
          force,
          // Reset progress fields
          processed: 0,
          synced: 0,
          skipped: 0,
          errors: 0,
          error: null,
          done: false,
        }, { merge: true })

        // Update local status immediately
        this.discourseSyncStatus = {
          ...this.discourseSyncStatus,
          running: true,
          run: true,
          done: false,
        }

        return true
      }
      catch (error) {
        console.error('[SystemConfig] Error triggering Discourse sync:', error)
        return false
      }
    },
  },
})
